import React, {
  Suspense,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, useProgress, Html } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { convertToSignedUrl } from "../../utils/wasabiHelper";
import AttachedModel3D from "./components/AttachChildModel";
import Fireworks from "./Fireworks";
import * as THREE from "three";

import introJs from "intro.js";
import "intro.js/introjs.css";
import {
  Button,
  Typography,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import MainLayout from "../../components/MainLayout";
import ContentGuide from "../../components/ContentGuide";
import YouTubePlayer from "./components/YouTubePlayer";
import VimeoPlayer from "./components/VimeoPlayer";
import { useAuth } from "../../auth/auth.context";
import { useUserProgress } from "../../hooks/useUserProgress";
import {
  getModule,
  getModuleSteps,
  getNextUncompletedModule,
  getModuleQuiz,
} from "../../services/moduleService";
import {
  updateModuleProgress,
  getModuleProgress,
} from "../../services/userProgressService";
import Hotspot from "./components/Hotspot";
import { useModuleQuiz } from "../../hooks/useModuleQuiz";

function ModelLoader({ progress }) {
  const circumference = 2 * Math.PI * 36; // radius = 36

  // Adjust calculation to account for strokeLinecap="round"
  // When progress reaches 100%, we want a complete circle
  const adjustedProgress = progress === 100 ? 99.9 : progress;
  const strokeDashoffset =
    circumference - (adjustedProgress / 100) * circumference;

  return (
    <Html center zIndexRange={-1}>
      <div
        style={{
          color: "#333",
          padding: "20px",
          fontSize: "14px",
          textAlign: "center",
          minWidth: "200px",
          backdropFilter: "blur(4px)",
        }}
      >
        <div
          style={{
            zIndex: -1,
            fontWeight: 600,
            marginBottom: "16px",
            color: "#1976d2",
          }}
        >
          Loading 3D Model
        </div>

        {/* SVG Circular Progress */}
        <div
          style={{
            position: "relative",
            width: "80px",
            height: "80px",
            margin: "0 auto 16px",
          }}
        >
          <svg
            width="80"
            height="80"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              transform: "rotate(-90deg)", // Start from top
            }}
          >
            {/* Background circle */}
            <circle
              cx="40"
              cy="40"
              r="36"
              fill="none"
              stroke="rgba(99, 94, 94, 0.1)"
              strokeWidth="4"
            />

            {/* Progress circle */}
            <circle
              cx="40"
              cy="40"
              r="36"
              fill="none"
              stroke="#1976d2"
              strokeWidth="4"
              strokeLinecap={progress === 100 ? "butt" : "round"}
              strokeDasharray={progress === 100 ? "none" : circumference}
              strokeDashoffset={progress === 100 ? 0 : strokeDashoffset}
              style={{
                transition: "stroke-dashoffset 0.3s ease",
              }}
            />
          </svg>

          {/* Progress text in center */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "18px",
              fontWeight: "700",
              color: "#1976d2",
            }}
          >
            {progress.toFixed(0)}%
          </div>
        </div>
      </div>
    </Html>
  );
}

// 3D Model Component - GLTF/GLB Only (similar to StepEditor)
function Model({ modelUrl, onModelClick, onLoadComplete }) {
  const meshRef = useRef();
  const [loadedModel, setLoadedModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadProgress, setLoadProgress] = useState(0);

  useEffect(() => {
    if (modelUrl) {
      setLoading(true);
      setError(null);
      setLoadedModel(null);
      setLoadProgress(0);

      const gltfLoader = new GLTFLoader();

      // Setup DRACO loader for compressed models
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(
        "https://www.gstatic.com/draco/versioned/decoders/1.5.6/"
      );
      gltfLoader.setDRACOLoader(dracoLoader);

      gltfLoader.load(
        modelUrl,
        // onLoad
        (gltf) => {
          // Scale model to fit in view
          const box = new THREE.Box3().setFromObject(gltf.scene);
          const size = box.getSize(new THREE.Vector3()).length();
          const scale = size > 0 ? 2 / size : 1;
          gltf.scene.scale.setScalar(scale);

          // Center the model
          const center = box.getCenter(new THREE.Vector3());
          gltf.scene.position.copy(center).multiplyScalar(-scale);

          // Enable shadows on all meshes
          gltf.scene.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });

          setLoadedModel(gltf);
          setLoading(false);

          // Notify parent that model has loaded
          if (onLoadComplete) {
            onLoadComplete(true);
          }
        },
        // onProgress
        (progress) => {
          if (progress.lengthComputable) {
            const percentComplete = (progress.loaded / progress.total) * 100;
            setLoadProgress(percentComplete);
          }
        },
        // onError
        (error) => {
          console.error("Error loading GLTF model:", error);
          setError(error);
          setLoading(false);

          // Notify parent that model failed to load
          if (onLoadComplete) {
            onLoadComplete(false);
          }
        }
      );
    } else {
      setLoadedModel(null);
      setLoading(false);
      setError(null);
    }
  }, [modelUrl]);

  useFrame(() => {
    if (meshRef.current) {
      // Any animations can go here
    }
  });

  // Show loading state
  if (loading) {
    return null;
  }

  // Show error state
  if (error) {
    return (
      <Html center zIndexRange={-1}>
        <div
          style={{
            color: "#d32f2f", // AirSmart error color
            padding: "24px",
            fontSize: "14px",
            textAlign: "center",
            minWidth: "250px",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: "8px",
              fontSize: "16px",
            }}
          >
            ❌ Failed to load 3D model
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "gray",
              lineHeight: "1.4",
            }}
          >
            Please check the model URL and CORS settings
          </div>
        </div>
      </Html>
    );
  }

  // Only render if we have a valid model
  if (!loadedModel) {
    return null;
  }

  return (
    <primitive
      ref={meshRef}
      object={loadedModel.scene}
      scale={[1, 1, 1]}
      onClick={onModelClick}
    />
  );
}

// Fallback component for when no model is loaded
function DefaultScene() {
  return (
    <mesh>
      <boxGeometry args={[2, 2, 2]} />
      <meshStandardMaterial color="#cccccc" />
    </mesh>
  );
}

// Updated ModelWithMemo component to use GLTF/GLB only
const ModelWithMemo = React.memo(function ModelWithMemo({
  modelUrl,
  onLoadComplete,
}) {
  // Check if URL is from Wasabi storage
  const isWasabiUrl = (url) => {
    if (!url) return false;
    return (
      url.includes("wasabisys.com") ||
      url.includes("airsmart") ||
      !url.includes("://")
    ); // If no protocol, assume it's a Wasabi path
  };

  // Process URL - create signed URL if it's a Wasabi URL
  let finalUrl = modelUrl;
  if (isWasabiUrl(finalUrl)) {
    finalUrl = convertToSignedUrl(finalUrl);
  }
  return (
    <Suspense fallback={null}>
      {finalUrl ? (
        <Model modelUrl={finalUrl} onLoadComplete={onLoadComplete} />
      ) : (
        <DefaultScene />
      )}
    </Suspense>
  );
});

// Camera controller component to handle step-specific camera settings
const CameraController = ({ cameraSettings, controlsRef }) => {
  const { camera } = useThree();

  // Effect to sync camera position when cameraSettings prop changes - matching admin_panel approach
  useEffect(() => {
    // Add a small delay to ensure OrbitControls is fully initialized
    const timer = setTimeout(() => {
      if (controlsRef.current && cameraSettings) {
        // Set camera position
        if (cameraSettings.position) {
          camera.position.set(
            cameraSettings.position[0],
            cameraSettings.position[1],
            cameraSettings.position[2]
          );
        }

        // Set camera target (look at)
        if (cameraSettings.target) {
          controlsRef.current.target.set(
            cameraSettings.target[0],
            cameraSettings.target[1],
            cameraSettings.target[2]
          );
        }

        // Update the controls to apply changes
        controlsRef.current.update();
      }
    }, 100); // Small delay to ensure OrbitControls is ready

    return () => clearTimeout(timer);
  }, [camera, cameraSettings, controlsRef]);

  return null;
};

// Reset camera function
const resetCameraToDefault = (camera, controlsRef, currentStep) => {
  if (!controlsRef.current) {
    console.warn("Controls not available");
    return false;
  }

  // Get default position and target from current step or use fallback
  const defaultPosition = currentStep?.camera?.position || [0, 0, 5];
  const defaultTarget = currentStep?.camera?.target || [0, 0, 0];
  // Set camera position
  camera.position.set(
    defaultPosition[0],
    defaultPosition[1],
    defaultPosition[2]
  );

  // Set camera target
  controlsRef.current.target.set(
    defaultTarget[0],
    defaultTarget[1],
    defaultTarget[2]
  );

  // Update controls to apply changes
  controlsRef.current.update();
  return true;
};

// Hotspots component - using new Hotspot component with tooltip
const HotspotsRenderer = React.memo(function HotspotsRenderer({
  hotspots = [],
  onHotspotClick,
}) {
  return (
    <>
      {hotspots &&
        hotspots.length > 0 &&
        hotspots.map((hotspot, index) => (
          <Hotspot
            key={hotspot.id || index}
            hotspot={hotspot}
            index={index}
            onHotspotClick={onHotspotClick}
          />
        ))}
    </>
  );
});

// Enhanced Image Viewer Component with Annotations
const ImageViewerWithAnnotations = React.memo(
  function ImageViewerWithAnnotations({ step }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);

    const handleLoad = () => {
      setLoading(false);
    };

    const handleError = () => {
      setLoading(false);
      setError("Failed to load image");
    };

    // Check if URL is from Wasabi storage
    const isWasabiUrl = (url) => {
      if (!url) return false;
      return (
        url.includes("wasabisys.com") ||
        url.includes("airsmart") ||
        !url.includes("://")
      );
    };

    // Process image URL - convert to signed URL if from Wasabi
    const processImageUrl = (imageUrl) => {
      if (!imageUrl) return null;

      if (isWasabiUrl(imageUrl)) {
        const signedUrl = convertToSignedUrl(imageUrl);
        return signedUrl;
      }

      return imageUrl;
    };

    const finalImageUrl = processImageUrl(step?.imageUrl);
    const imageAnnotations = step?.imageAnnotations || [];

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
              Loading image...
            </Typography>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
          >
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Image with Annotations Wrapper */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "auto",
            maxWidth: "100%",
            maxHeight: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={finalImageUrl}
            alt={step.title}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "100%",
              height: "auto",
              objectFit: "contain",
              borderRadius: "8px",
              display: loading ? "none" : "block",
            }}
            onLoad={handleLoad}
            onError={handleError}
          />

          {/* Image Annotations Overlay */}
          {!loading &&
            imageAnnotations.length > 0 &&
            imageAnnotations.map((annotation, index) => (
              <Box
                key={annotation.id || index}
                sx={{
                  position: "absolute",
                  left: `${annotation.position?.x || 50}%`,
                  top: `${annotation.position?.y || 50}%`,
                  transform: "translate(-50%, -50%)",
                  zIndex: selectedAnnotation?.id === annotation.id ? 20 : 10,
                  pointerEvents: "auto",
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  color={"blue"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAnnotation(
                      selectedAnnotation?.id === annotation.id ? null : annotation
                    );
                  }}
                  sx={{
                    minWidth: "auto",
                    borderRadius: "50%",
                    width: { xs: 26, sm: 32 },
                    height: { xs: 26, sm: 32 },
                    fontSize: { xs: "10px", sm: "12px" },
                    fontWeight: "bold",
                    bgcolor:
                      annotation.style?.color === "primary" ? "#2196f3" :
                        annotation.style?.color === "secondary" ? "#9c27b0" :
                          annotation.style?.color === "error" ? "#f44336" :
                            annotation.style?.color === "warning" ? "#ff9800" :
                              annotation.style?.color === "success" ? "#4caf50" : "primary.main",
                    "&:hover": {
                      transform: "scale(1.1)",
                      transition: "transform 0.2s",
                      bgcolor:
                        annotation.style?.color === "primary" ? "#1976d2" :
                          annotation.style?.color === "secondary" ? "#7b1fa2" :
                            annotation.style?.color === "error" ? "#d32f2f" :
                              annotation.style?.color === "warning" ? "#f57c00" :
                                annotation.style?.color === "success" ? "#388e3c" : "primary.dark",
                    },
                  }}
                >
                  {index + 1}
                </Button>

                {/* Annotation Tooltip */}
                {selectedAnnotation?.id === annotation.id && (
                  <Paper
                    elevation={4}
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      mt: { xs: 0.5, sm: 1.5 },
                      p: { xs: 1, sm: 2 },
                      minWidth: { xs: "130px", sm: "200px" },
                      maxWidth: { xs: "130px", sm: "280px" },
                      width: { xs: "max-content", sm: "auto" },
                      zIndex: 20,
                      bgcolor: "background.paper",
                      color: "text.primary",
                      borderRadius: 2,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          fontSize: { xs: "11px", sm: "0.875rem" },
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          wordBreak: "break-word"
                        }}
                      >
                        {annotation.title}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAnnotation(null);
                        }}
                        sx={{ p: 0.5, ml: 1, mt: -0.5, mr: -0.5 }}
                      >
                        ✕
                      </IconButton>
                    </Box>
                    {annotation.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: { xs: "10px", sm: "0.875rem" },
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          wordBreak: "break-word"
                        }}
                      >
                        {annotation.description}
                      </Typography>
                    )}
                  </Paper>
                )}
              </Box>
            ))}
        </Box>

        {/* Annotations Info Badge */}
        {/* {imageAnnotations.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            background: 'rgba(25, 118, 210, 0.9)',
            color: 'white',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            fontSize: '12px',
            fontWeight: 'bold'
          }}
        >
          📍 {imageAnnotations.length} annotation{imageAnnotations.length !== 1 ? 's' : ''}
        </Box>
      )} */}
      </Box>
    );
  }
);

// Enhanced Video Viewer Component with Annotations
const VideoViewerWithAnnotations = React.memo(
  function VideoViewerWithAnnotations({ step }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);
    const [annotationPosition, setAnnotationPosition] = useState({
      x: 0,
      y: 0,
    });
    const videoRef = useRef(null);

    // Memoize URLs to prevent recreation on every render
    const finalVideoUrl = useMemo(() => {
      const processVideoUrl = (videoUrl) => {
        if (!videoUrl) return null;

        const isWasabiUrl = (url) => {
          if (!url) return false;
          return (
            url.includes("wasabisys.com") ||
            url.includes("airsmart") ||
            !url.includes("://")
          );
        };

        if (isWasabiUrl(videoUrl)) {
          const signedUrl = convertToSignedUrl(videoUrl);
          return signedUrl;
        }

        return videoUrl;
      };

      return processVideoUrl(step?.videoUrl);
    }, [step?.videoUrl]);

    // Memoize video type detection and extract video IDs
    const { isEmbed, embedUrl, videoType, videoId } = useMemo(() => {
      const getVideoInfo = (url) => {
        if (!url)
          return {
            isEmbed: false,
            embedUrl: null,
            videoType: null,
            videoId: null,
          };

        // YouTube URLs
        if (url.includes("youtube.com/watch?v=")) {
          const id = url.split("v=")[1]?.split("&")[0];
          return {
            isEmbed: true,
            embedUrl: `https://www.youtube.com/embed/${id}`,
            videoType: "youtube",
            videoId: id,
          };
        }
        if (url.includes("youtu.be/")) {
          const id = url.split("youtu.be/")[1]?.split("?")[0];
          return {
            isEmbed: true,
            embedUrl: `https://www.youtube.com/embed/${id}`,
            videoType: "youtube",
            videoId: id,
          };
        }
        if (url.includes("youtube.com/embed/")) {
          const id = url.split("embed/")[1]?.split("?")[0];
          return {
            isEmbed: true,
            embedUrl: url,
            videoType: "youtube",
            videoId: id,
          };
        }

        // Vimeo URLs
        if (url.includes("vimeo.com/") && !url.includes("player.vimeo.com")) {
          const id = url.split("vimeo.com/")[1]?.split("?")[0];
          return {
            isEmbed: true,
            embedUrl: `https://player.vimeo.com/video/${id}`,
            videoType: "vimeo",
            videoId: id,
          };
        }
        if (url.includes("player.vimeo.com/video/")) {
          const id = url.split("video/")[1]?.split("?")[0];
          return {
            isEmbed: true,
            embedUrl: url,
            videoType: "vimeo",
            videoId: id,
          };
        }

        return {
          isEmbed: false,
          embedUrl: null,
          videoType: null,
          videoId: null,
        };
      };

      return getVideoInfo(finalVideoUrl);
    }, [finalVideoUrl]);

    // Memoize video annotations to prevent array recreation
    const videoAnnotations = useMemo(
      () => step?.videoAnnotations || [],
      [step?.videoAnnotations]
    );

    // Memoize event handlers to prevent recreation
    const handleLoad = useCallback(() => {
      setLoading(false);
    }, []);

    const handleError = useCallback(() => {
      setLoading(false);
      setError("Failed to load video");
    }, []);

    // Handle time update for video annotations
    const handleTimeUpdate = useCallback(() => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
      }
    }, []);

    // Handle time update for embed players (YouTube, Vimeo)
    const handleEmbedTimeUpdate = useCallback((time) => {
      setCurrentTime(time);
    }, []);

    // Handle embed player ready
    const handleEmbedReady = useCallback(() => {
      setLoading(false);
    }, []);

    // Handle embed player error
    const handleEmbedError = useCallback((error) => {
      setLoading(false);
      setError("Failed to load video");
      console.error("Embed player error:", error);
    }, []);

    // Only setup event listeners when video ref changes and it's not embed
    useEffect(() => {
      const video = videoRef.current;
      if (video && !isEmbed) {
        video.addEventListener("timeupdate", handleTimeUpdate);
        return () => {
          video.removeEventListener("timeupdate", handleTimeUpdate);
        };
      }
    }, [isEmbed, handleTimeUpdate]);

    // Reset loading state when video URL changes
    useEffect(() => {
      if (finalVideoUrl) {
        setLoading(true);
        setError(null);
      }
    }, [finalVideoUrl]);

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
              Loading video...
            </Typography>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
          >
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Video Player Wrapper */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "auto",
            maxWidth: "100%",
            maxHeight: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            aspectRatio: isEmbed ? "16/9" : "auto", // Maintain aspect ratio for embeds
          }}
        >
          {isEmbed ? (
            // Render API-enabled players for YouTube and Vimeo with time tracking
            videoType === "youtube" ? (
              <YouTubePlayer
                key={videoId}
                videoId={videoId}
                onTimeUpdate={handleEmbedTimeUpdate}
                onReady={handleEmbedReady}
                onError={handleEmbedError}
                autoPlay={step?.autoPlay}
              />
            ) : videoType === "vimeo" ? (
              <VimeoPlayer
                key={videoId}
                videoId={videoId}
                onTimeUpdate={handleEmbedTimeUpdate}
                onReady={handleEmbedReady}
                onError={handleEmbedError}
                autoPlay={step?.autoPlay}
              />
            ) : (
              // Fallback to iframe for other embed types
              <iframe
                key={embedUrl}
                src={embedUrl}
                width="100%"
                height="100%"
                style={{
                  border: "none",
                  borderRadius: "8px",
                  minHeight: "400px",
                  display: loading ? "none" : "block",
                }}
                onLoad={handleLoad}
                onError={handleError}
                title={`Video for ${step?.title || "Step"}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )
          ) : (
            // Render video element for direct video files
            <video
              key={finalVideoUrl} // Add key to force remount only when URL actually changes
              ref={videoRef}
              src={finalVideoUrl}
              controls
              autoPlay={step?.autoPlay || false}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                width: "100%",
                height: "auto",
                objectFit: "contain",
                borderRadius: "8px",
                display: loading ? "none" : "block",
              }}
              onLoadedData={(e) => {
                handleLoad();
                if (step?.autoPlay && videoRef.current) {
                  try {
                    const p = videoRef.current.play();
                    if (p && typeof p.then === 'function') {
                      p.catch(() => { });
                    }
                  } catch (_) { }
                }
              }}
              onError={handleError}
            />
          )}

          {/* Video Annotations Overlay - Show for all video types with time tracking */}
          {!loading &&
            videoAnnotations.length > 0 &&
            videoAnnotations.map((annotation, index) => {
              // Show annotations based on current time for all video types
              // YouTube and Vimeo now support time tracking via APIs
              const isActive =
                currentTime >= (annotation.startTime || 0) &&
                currentTime <= (annotation.endTime || Infinity);
              if (!isActive) return null;

              return (
                <Box
                  key={annotation.id || index}
                  sx={{
                    position: "absolute",
                    left: `${annotation.position?.x || 50}%`,
                    top: `${annotation.position?.y || 50}%`,
                    transform: "translate(-50%, -50%)",
                    zIndex: selectedAnnotation?.id === annotation.id ? 20 : 10,
                    pointerEvents: "auto",
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    color={"blue"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAnnotation(
                        selectedAnnotation?.id === annotation.id ? null : annotation
                      );
                    }}
                    sx={{
                      minWidth: "auto",
                      borderRadius: "50%",
                      width: 32,
                      height: 32,
                      fontSize: "12px",
                      fontWeight: "bold",
                      animation: "pulse 2s infinite",
                      bgcolor:
                        annotation.style?.color === "primary" ? "#2196f3" :
                          annotation.style?.color === "secondary" ? "#9c27b0" :
                            annotation.style?.color === "error" ? "#f44336" :
                              annotation.style?.color === "warning" ? "#ff9800" :
                                annotation.style?.color === "success" ? "#4caf50" : "primary.main",
                      "&:hover": {
                        transform: "scale(1.1)",
                        transition: "transform 0.2s",
                        bgcolor:
                          annotation.style?.color === "primary" ? "#1976d2" :
                            annotation.style?.color === "secondary" ? "#7b1fa2" :
                              annotation.style?.color === "error" ? "#d32f2f" :
                                annotation.style?.color === "warning" ? "#f57c00" :
                                  annotation.style?.color === "success" ? "#388e3c" : "primary.dark",
                      },
                      "@keyframes pulse": {
                        "0%": {
                          transform: "scale(1)",
                        },
                        "50%": {
                          transform: "scale(1.1)",
                        },
                        "100%": {
                          transform: "scale(1)",
                        },
                      },
                    }}
                  >
                    {index + 1}
                  </Button>

                  {/* Annotation Tooltip */}
                  {selectedAnnotation?.id === annotation.id && (
                    <Paper
                      elevation={4}
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        mt: { xs: 0.5, sm: 1.5 },
                        p: { xs: 1, sm: 2 },
                        minWidth: { xs: "130px", sm: "200px" },
                        maxWidth: { xs: "130px", sm: "280px" },
                        width: { xs: "max-content", sm: "auto" },
                        zIndex: 20,
                        bgcolor: "background.paper",
                        color: "text.primary",
                        borderRadius: 2,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: { xs: 0.5, sm: 1 },
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: "primary.main",
                            fontSize: { xs: "11px", sm: "0.875rem" },
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            wordBreak: "break-word"
                          }}
                        >
                          {annotation.title}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAnnotation(null);
                          }}
                          sx={{ p: 0.5, ml: 1, mt: -0.5, mr: -0.5 }}
                        >
                          ✕
                        </IconButton>
                      </Box>
                      {annotation.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mb: 1,
                            fontSize: { xs: "10px", sm: "0.875rem" },
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            wordBreak: "break-word"
                          }}
                        >
                          {annotation.description}
                        </Typography>
                      )}
                      {annotation.startTime !== undefined && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          ⏱️ {Math.floor(annotation.startTime)}s -{" "}
                          {Math.floor(annotation.endTime)}s
                        </Typography>
                      )}
                    </Paper>
                  )}
                </Box>
              );
            })}
        </Box>

        {/* Video Annotations Info Badge */}
        {/* {videoAnnotations.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            background: 'rgba(25, 118, 210, 0.9)',
            color: 'white',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            fontSize: '12px',
            fontWeight: 'bold'
          }}
        >
          🎬 {videoAnnotations.length} annotation{videoAnnotations.length !== 1 ? 's' : ''}
        </Box>
      )} */}
      </Box>
    );
  }
);

// Enhanced Media Viewer Component that chooses the right viewer
const MediaViewer = React.memo(function MediaViewer({ step }) {
  const hasVideo = step?.videoUrl;
  const hasImage = step?.imageUrl;

  if (!hasVideo && !hasImage) {
    return null;
  }

  if (hasVideo) {
    return <VideoViewerWithAnnotations step={step} />;
  }

  if (hasImage) {
    return <ImageViewerWithAnnotations step={step} />;
  }

  return null;
});

function ModuleViewer() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    isModuleCompleted: checkModuleCompleted,
    getProgressModuleVersion,
  } = useUserProgress();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [module, setModule] = useState(null);
  const [steps, setSteps] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [moduleCompleted, setModuleCompleted] = useState(false);
  const [nextModule, setNextModule] = useState(null);
  const [hasQuiz, setHasQuiz] = useState(false); // New state to track if module has quiz
  const [quizLoading, setQuizLoading] = useState(false); // Loading state for quiz check
  const [initialLoadComplete, setInitialLoadComplete] = useState(false); // Track initial load
  const [showContentGuide, setShowContentGuide] = useState(false); // Content guide state
  const [attachedModels, setAttachedModels] = useState([]);
  const [mainModelLoaded, setMainModelLoaded] = useState(false); // Track main model loading
  const [modelLoadError, setModelLoadError] = useState(false); // Track model loading error
  const [showFireworks, setShowFireworks] = useState(false); // Track fireworks display
  const { data: quizData, isLoading: isQuizLoading, error: quizError } = useModuleQuiz(moduleId);
  // Refs to track progress updates
  const prevStepRef = useRef(null);
  // Ref for OrbitControls - matching EditStepEditor approach
  const controlsRef = useRef();
  const isUpdatingProgressRef = useRef(false); // Prevent multiple simultaneous updates

  // Handler for navigating to next module
  const handleNextModule = () => {
    if (nextModule) {
      navigate(`/module/${nextModule.id}`);
    } else {
      navigate("/");
    }
  };

  // Handler for fireworks completion
  const handleFireworksComplete = () => {
    setShowFireworks(false);
  };

  // Handler for hotspot click
  const handleHotspotClick = useCallback((hotspot) => {
    // You can add additional logic here if needed
    // For example, tracking analytics, showing additional info, etc.
  }, []);

  // Handler for main model load complete
  const handleMainModelLoadComplete = useCallback((success) => {
    setMainModelLoaded(success);
    setModelLoadError(!success);
  }, []);

  // Effect to load next module when current module is completed
  useEffect(() => {
    const loadNextModule = async () => {
      if (moduleCompleted && currentUser) {
        try {
          const nextModuleData = await getNextUncompletedModule(
            moduleId,
            checkModuleCompleted,
            getProgressModuleVersion
          );
          setNextModule(nextModuleData);
        } catch (error) {
          console.error("Error loading next module:", error);
        }
      }
    };

    loadNextModule();
  }, [
    moduleCompleted,
    moduleId,
    currentUser,
    checkModuleCompleted,
    getProgressModuleVersion,
  ]);

  // Load module and steps data
  useEffect(() => {
    let isMounted = true;

    const loadModuleData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get module data
        const moduleData = await getModule(moduleId);
        if (!isMounted) return;

        if (!moduleData) {
          setError("Module not found");
          setLoading(false);
          return;
        }
        setModule(moduleData);

        // Get steps data
        // const stepsData = await getModuleSteps(moduleId);
        const stepsData = moduleData.steps || [];
        if (!isMounted) return;
        // console.log("module Data");
        // console.log(moduleData);
        // console.log("step data");
        // console.log(stepsData);
        if (stepsData.length === 0) {
          setError("No steps found for this module");
          setLoading(false);
          return;
        }

        // Steps are already sorted by order from backend
        setSteps(stepsData);

        // Set default active step
        setActiveStep(0);
        setModuleCompleted(false);

        // Show content guide for "introduction" module
        if (moduleData.name === "Introduction" && currentUser) {
          // Small delay to ensure UI is rendered
          setTimeout(() => {
            setShowContentGuide(true);
          }, 1000);
        }



        if (isMounted) {
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading module data:", err);
        if (isMounted) {
          setError("Failed to load module data");
          setLoading(false);
        }
      }
    };

    loadModuleData();

    return () => {
      isMounted = false;
    };
  }, [moduleId]); // Chỉ phụ thuộc vào moduleId để tránh gọi API nhiều lần

  useEffect(() => {
    setQuizLoading(isQuizLoading);
    if (!quizLoading) {
      setHasQuiz(Array.isArray(quizData) ? quizData.length > 0 : !!quizData);
    }
    setQuizLoading(isQuizLoading);
  }, [quizData, quizLoading]);


  // Load user progress separately and set initial activeStep correctly
  useEffect(() => {
    const loadUserProgress = async () => {
      if (!currentUser || !steps.length || initialLoadComplete) {
        return;
      }

      try {
        // Lấy tiến trình hiện tại của người dùng cho module này
        const moduleProgress = await getModuleProgress(
          currentUser.uid,
          moduleId
        );

        if (
          moduleProgress &&
          (moduleProgress.stepId || moduleProgress.currentStepId)
        ) {
          // Sử dụng currentStepId hoặc stepId tùy thuộc vào cấu trúc dữ liệu
          let savedStepId =
            moduleProgress.currentStepId || moduleProgress.stepId;

          const moduleProgressVersion = moduleProgress.version ?? 1;
          if (
            moduleProgressVersion < module.version &&
            savedStepId === "completed"
          )
            savedStepId = steps[0];

          // Kiểm tra xem module đã hoàn thành chưa
          if (savedStepId === "completed") {
            // Nếu module đã hoàn thành, đặt activeStep là bước cuối cùng
            setActiveStep(steps.length - 1);
            setModuleCompleted(true);
          } else {
            // Tìm index của step trong danh sách steps
            const stepIndex = steps.findIndex(
              (step) => step.id === savedStepId
            );
            if (stepIndex !== -1) {
              setActiveStep(stepIndex);
            } else {
              // Nếu không tìm thấy step, bắt đầu từ step đầu tiên
              setActiveStep(0);
            }
            setModuleCompleted(false);
          }
        } else {
          // Nếu chưa có tiến trình, bắt đầu từ step đầu tiên
          setActiveStep(0);
          setModuleCompleted(false);
        }

        // Đánh dấu đã hoàn thành việc load initial data
        setInitialLoadComplete(true);
      } catch (err) {
        console.error("Error getting module progress:", err);
        setActiveStep(0);
        setModuleCompleted(false);
        setInitialLoadComplete(true);
      }
    };

    loadUserProgress();
  }, [currentUser, moduleId, steps, initialLoadComplete]);

  // Cache để tránh gọi API nhiều lần cho cùng một step
  const progressCacheRef = useRef({});

  // Update user progress when active step changes - FIXED LOGIC
  const updateProgress = useCallback(async () => {
    // Chỉ update progress sau khi đã load xong initial data
    if (
      !currentUser ||
      !module ||
      steps.length === 0 ||
      activeStep >= steps.length ||
      !initialLoadComplete
    ) {
      return;
    }

    // Prevent concurrent updates
    if (isUpdatingProgressRef.current) {
      return;
    }

    const currentStep = steps[activeStep];
    const progressKey = `${moduleId}_${currentStep.id}`;

    // Kiểm tra cache trước khi gọi API
    if (progressCacheRef.current[progressKey]) {
      return;
    }

    // Only update when step changes
    if (prevStepRef.current !== progressKey) {
      prevStepRef.current = progressKey;
      isUpdatingProgressRef.current = true;

      try {
        let moduleProgress = null;
        let savedStepId = null;
        try {
          moduleProgress = await getModuleProgress(
            currentUser.uid,
            moduleId
          );
          savedStepId =
            moduleProgress?.currentStepId || moduleProgress?.stepId;
          const moduleProgressVersion = moduleProgress?.version ?? 1;
          const moduleVersion = module?.version ?? 1;
          if (
            moduleProgressVersion < moduleVersion &&
            savedStepId === "completed"
          ) {
            savedStepId = steps[0];
          }
        } catch (_e) {
          savedStepId = null;
        }
        // Nếu module đã hoàn thành, không cập nhật lại
        if (savedStepId === "completed") {
          setModuleCompleted(true);
          progressCacheRef.current[progressKey] = true;
          setSnackbarMessage("Module already completed");
          setSnackbarOpen(true);
          return;
        }

        // Chỉ update nếu step hiện tại khác với step đã lưu hoặc chưa có progress
        if (!savedStepId || savedStepId !== currentStep.id) {
          await updateModuleProgress(
            currentUser.uid,
            moduleId,
            currentStep.id,
            false, // not completed yet
            null, // no quiz score yet
            module?.version ?? 1
          );

          // Cache kết quả thành công
          progressCacheRef.current[progressKey] = true;

          setSnackbarMessage(`Progress updated to ${currentStep.title}`);
          setSnackbarOpen(true);
        } else {
          // Nếu step đã được lưu rồi, chỉ cache kết quả
          progressCacheRef.current[progressKey] = true;
        }
      } catch (err) {
        console.error("Error updating progress:", err);
        setSnackbarMessage("Error updating progress");
        setSnackbarOpen(true);
      } finally {
        isUpdatingProgressRef.current = false;
      }
    }
  }, [activeStep, currentUser, moduleId, module, steps, initialLoadComplete]);
  useEffect(() => {
    // Chỉ cập nhật khi có đủ dữ liệu
    if (!currentUser || !module || !steps.length) {
      return;
    }

    // Lấy thông tin về bước hiện tại
    const currentStep = steps[activeStep];
    if (!currentStep) {
      return;
    }

    // console.log(currentStep);
    if (currentStep.attachedModels) {
      setAttachedModels(currentStep.attachedModels);
    } else {
      setAttachedModels([]);
    }

    if (currentStep.videoUrl || currentStep.imageUrl) {
      setMainModelLoaded(false);
    }

    // Tạo key duy nhất cho mỗi bước
    const stepKey = `${moduleId}_${currentStep.id}`;

    // Kiểm tra cache trước khi gọi API
    if (progressCacheRef.current[stepKey]) {
      return;
    }
    // Debounce để tránh gọi API quá nhiều lần
    const timer = setTimeout(() => {
      updateProgress();
    }, 300); // Giảm thời gian debounce

    return () => {
      clearTimeout(timer);
    };
  }, [activeStep, currentUser, moduleId, module, steps, updateProgress]);

  // Handler for completing module when no quiz
  const handleCompleteModule = async () => {
    try {
      // Mark module as completed
      await updateModuleProgress(
        currentUser.uid,
        moduleId,
        "completed", // Special stepId to indicate completion
        true, // Module is completed
        null, // No quiz score
        module?.version ?? 1
      );

      setModuleCompleted(true);
      setSnackbarMessage("Module completed successfully!");
      setSnackbarOpen(true);

      // Load next module
      try {
        const nextModuleData = await getNextUncompletedModule(
          moduleId,
          checkModuleCompleted
        );
        setNextModule(nextModuleData);

        // Nếu không có module tiếp theo, hiển thị pháo hoa chúc mừng
        if (!nextModuleData) {
          setTimeout(() => {
            setShowFireworks(true);
          }, 1000); // Delay 1 giây để người dùng thấy thông báo hoàn thành trước
        }
      } catch (error) {
        console.error("Error loading next module:", error);
        // Nếu có lỗi khi load next module, cũng hiển thị pháo hoa (có thể là module cuối)
        setTimeout(() => {
          setShowFireworks(true);
        }, 1000);
      }
    } catch (error) {
      console.error("Error completing module:", error);
      setSnackbarMessage("Error completing module");
      setSnackbarOpen(true);
    }
  };

  // Navigation handlers - FIXED LOGIC
  const handleNext = () => {
    // Đơn giản hóa logic: chỉ chuyển đến step tiếp theo
    // Việc update progress sẽ được xử lý bởi useEffect khác
    setActiveStep((prevActiveStep) => {
      const nextStep = prevActiveStep + 1;
      if (nextStep < steps.length) {
        // Clear cache cho step tiếp theo để đảm bảo nó được cập nhật
        const nextStepKey = `${moduleId}_${steps[nextStep].id}`;
        delete progressCacheRef.current[nextStepKey];
        return nextStep;
      }
      return prevActiveStep; // Không thay đổi nếu đã ở step cuối
    });
  };

  const handleBack = () => {
    // Đơn giản hóa logic: chỉ chuyển đến step trước đó
    setActiveStep((prevActiveStep) => {
      const previousStep = prevActiveStep - 1;
      if (previousStep >= 0) {
        // Clear cache cho step trước đó để đảm bảo nó được cập nhật lại
        const previousStepKey = `${moduleId}_${steps[previousStep].id}`;
        delete progressCacheRef.current[previousStepKey];
        return previousStep;
      }
      return prevActiveStep; // Không thay đổi nếu đã ở step đầu
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Start interactive tour
  const startIntro = () => {
    const currentStep = steps[activeStep];

    if (!currentStep) {
      setSnackbarMessage("No tour available for this step");
      setSnackbarOpen(true);
      return;
    }
    // Wait for elements to be rendered
    setTimeout(() => {
      let introSteps = [];

      // Only use hotspots for tour - introjsSteps functionality disabled
      if (currentStep.hotspots && currentStep.hotspots.length > 0) {
        // Add module viewer container as first step
        const viewerContainer = document.querySelector(
          "#module-viewer-container"
        );
        if (viewerContainer) {
          introSteps.push({
            element: viewerContainer,
            intro:
              "This is the 3D model viewer area. You can rotate, zoom, and pan the model.",
            position: "bottom",
          });
        }

        // Add hotspots to tour
        currentStep.hotspots.forEach((hotspot) => {
          const element = document.querySelector(`#${hotspot.id}`);
          if (element) {
            introSteps.push({
              element,
              intro:
                hotspot.description ||
                hotspot.intro ||
                `This is ${hotspot.label || hotspot.id}`,
              position: hotspot.introPosition || "bottom",
            });
          } else {
            console.warn(`Hotspot element not found: #${hotspot.id}`);
          }
        });
      }

      // Always add tour button as last step
      const tourButton = document.querySelector("#tour-button");
      if (tourButton) {
        introSteps.push({
          element: tourButton,
          intro: "Click here to restart the tour anytime.",
          position: "left",
        });
      }

      if (introSteps.length === 0) {
        setSnackbarMessage("No tour available for this step");
        setSnackbarOpen(true);
        return;
      }
      // Start IntroJS with improved options
      introJs()
        .setOptions({
          steps: introSteps,
          nextLabel: "Next",
          prevLabel: "Back",
          skipLabel: "Skip",
          doneLabel: "Done",
          // Improved positioning options
          scrollToElement: true,
          showStepNumbers: false,
          exitOnOverlayClick: false,
          exitOnEsc: true,
          helperElementPadding: 10,
          positionPrecedence: ["bottom", "top", "right", "left"],
          // Add callbacks for debugging
          onbeforechange: function (targetElement) {
            if (targetElement) {
              // Ensure element is visible
              targetElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          },
          onchange: function (targetElement) { },
        })
        .start();
    }, 1000); // Increased timeout to ensure 3D elements are rendered
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/")}
          sx={{ mt: 2 }}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  // No content state
  if (!module || steps.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No content available</Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/")}
          sx={{ mt: 2 }}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  const currentStep = steps[activeStep];

  return (
    <MainLayout>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        {/* Module Header */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom color="text.primary">
              {module.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {module.description}
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
          }}
        >
          {/* Media/3D Viewer */}
          <Paper
            sx={{
              p: 0,
              position: "relative",
              height: { xs: "300px", sm: "400px", md: "70vh" },
              width: { xs: "100%", md: "60%" },
              mb: { xs: 3, md: 0 },
              overflow: "hidden",
            }}
            id="module-viewer-container"
          >
            {/* <IconButton
              id="tour-button"
              color="primary"
              onClick={startIntro}
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                zIndex: 1000,
                bgcolor: "rgba(255,255,255,0.7)",
              }}
              aria-label="Start Tour"
            >
              <HelpOutlineIcon />
            </IconButton> */}

            {/* Reset Camera Button - NEW */}
            {!currentStep?.videoUrl && !currentStep?.imageUrl && (
              <Tooltip title="Reset Camera">
                <IconButton
                  color="primary"
                  onClick={() => {
                    const { camera } = controlsRef.current
                      ? { camera: controlsRef.current.object }
                      : {};
                    if (camera && controlsRef.current) {
                      const success = resetCameraToDefault(
                        camera,
                        controlsRef,
                        currentStep
                      );
                      if (success) {
                        setSnackbarMessage("Camera reset to default position");
                        setSnackbarOpen(true);
                      }
                    } else {
                      setSnackbarMessage("Camera controls not available");
                      setSnackbarOpen(true);
                    }
                  }}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    zIndex: 1000,
                    bgcolor: "rgba(255,255,255,0.7)",
                  }}
                  aria-label="Reset Camera"
                >
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Conditional rendering: Video/Image OR 3D Model */}
            {currentStep?.videoUrl || currentStep?.imageUrl ? (
              // Display video or image if available
              <Box sx={{ width: "100%", height: "100%", p: { xs: 0, md: 2 } }}>
                <MediaViewer step={currentStep} />
              </Box>
            ) : (
              <>
                {!mainModelLoaded && !modelLoadError && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 1000,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                      Loading 3D Model...
                    </Typography>
                  </Box>
                )}
                <Canvas
                  camera={{
                    position: [0, 0, 5], // Default position, will be overridden by CameraController
                    fov: 60,
                  }}
                  style={{ height: "100%", width: "100%" }}
                  frameloop="demand"
                  gl={{ antialias: true, alpha: true }}
                >
                  {/* Complete lighting setup - matching StepEditor exactly */}
                  <ambientLight intensity={0.6} />
                  <directionalLight
                    position={[10, 10, 5]}
                    intensity={1.2}
                    castShadow
                  />
                  <directionalLight position={[-10, -10, -5]} intensity={0.8} />
                  <directionalLight position={[0, 10, 10]} intensity={0.8} />
                  <pointLight position={[0, 10, 0]} intensity={0.7} />
                  <pointLight position={[5, 0, 5]} intensity={0.5} />
                  <pointLight position={[-5, 0, -5]} intensity={0.5} />

                  {/* OrbitControls - placed at the correct position with ref */}
                  <OrbitControls
                    ref={controlsRef}
                    enablePan={false}
                    enableZoom={true}
                    enableRotate={true}
                    enableDamping={true}
                    dampingFactor={0.05}
                    rotateSpeed={1.0}
                    zoomSpeed={1.5}
                    panSpeed={0.8}
                    autoRotate={false}
                  />

                  {/* Camera controller for step-specific camera settings */}
                  <CameraController
                    cameraSettings={currentStep?.camera}
                    controlsRef={controlsRef}
                  />

                  {/* 3D Model with proper Suspense fallback */}
                  <Suspense
                    fallback={<primitive object={new THREE.Object3D()} />}
                  >
                    <ModelWithMemo
                      key={module.modelUrl}
                      modelUrl={module.modelUrl}
                      modelType={module.modelType || "auto"}
                      onLoadComplete={handleMainModelLoadComplete}
                    />
                  </Suspense>

                  {/* Attached Models - Only render after main model is loaded */}
                  {mainModelLoaded &&
                    attachedModels.map((attachedModel) => {
                      // Create ref for this model if it doesn't exist
                      // if (!attachedModelRefs.current[attachedModel.id]) {
                      //   attachedModelRefs.current[attachedModel.id] =
                      //     React.createRef();
                      // }

                      // console.log(attachedModels);
                      // console.log(attachedModel);

                      return (
                        <AttachedModel3D
                          key={attachedModel.id}
                          // ref={attachedModelRefs.current[attachedModel.id]}
                          attachedModel={attachedModel}
                        // onSelect={onSelectAttachedModel}
                        />
                      );
                    })}

                  {/* Render hotspots separately to avoid model reload */}

                  <HotspotsRenderer
                    hotspots={
                      currentStep && currentStep.hotspots
                        ? currentStep.hotspots
                        : []
                    }
                    onHotspotClick={handleHotspotClick}
                  />
                </Canvas>
              </>
            )}
          </Paper>

          {/* Steps */}
          <Paper
            sx={{
              p: 3,
              width: { xs: "100%", md: "40%" },
              overflow: "auto",
              maxHeight: "70vh",
            }}
          >
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.id}>
                  <StepLabel>{step.title}</StepLabel>
                  <StepContent>
                    <Typography>{step.content}</Typography>

                    {/* Media type indicator */}
                    {/* {(step.videoUrl || step.imageUrl) && (
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{
                            bgcolor: "primary.main",
                            color: "white",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: "0.75rem",
                          }}
                        >
                          {step.videoUrl
                            ? "🎬 Video Content"
                            : "🖼️ Image Content"}
                        </Typography>
                      </Box>
                    )} */}

                    <Box
                      sx={{
                        mb: 2,
                        mt: 2,
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {index === steps.length - 1 ? (
                        moduleCompleted ? (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              disabled
                              endIcon={<CheckCircleOutlineIcon />}
                            >
                              Module Completed
                            </Button>
                            {nextModule && (
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNextModule}
                                endIcon={<ArrowForwardIcon />}
                                sx={{ ml: 1 }}
                              >
                                Next Module
                              </Button>
                            )}
                          </>
                        ) : // Check if module has quiz
                          hasQuiz ? (
                            <Button
                              variant="contained"
                              color="success"
                              onClick={() => navigate(`/module/${moduleId}/quiz`)}
                              endIcon={<ArrowForwardIcon />}
                              disabled={quizLoading}
                            >
                              {quizLoading ? "Checking..." : "Take Quiz"}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleCompleteModule}
                              endIcon={<CheckCircleOutlineIcon />}
                              disabled={quizLoading}
                            >
                              {quizLoading ? "Checking..." : "Complete Module"}
                            </Button>
                          )
                      ) : (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          endIcon={<ArrowForwardIcon />}
                        >
                          Continue
                        </Button>
                      )}
                      <Button
                        disabled={index === 0}
                        onClick={handleBack}
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                      >
                        Back
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Paper>
        </Box>

        {/* Snackbar for global messages */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={
            isMobile
              ? { vertical: "top", horizontal: "center" }
              : { vertical: "bottom", horizontal: "left" }
          }
          sx={
            isMobile
              ? {
                top: "30px !important", // Clear the header
                left: "50% !important",
                transform: "translateX(-50%) !important",
                width: "calc(100% - 32px)",
                maxWidth: "400px",
              }
              : {}
          }
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="info"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Content Guide for question-3d module */}
        <ContentGuide
          open={showContentGuide}
          onClose={() => setShowContentGuide(false)}
          moduleId={moduleId}
        />
      </Container>

      {/* Fireworks celebration for completing all modules */}
      <Fireworks show={showFireworks} onComplete={handleFireworksComplete} />
    </MainLayout>
  );
}

export default ModuleViewer;
